import { useModuleBEM } from "@b2bportal/core-themes";
import type { CfarContract, Reservation } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import styles from "./BookingDetails.module.scss";
import { useBookingDetails } from "./useBookingDetails";
import CfarTicketIcon from "@/assets/icons/ui/cfar-ticket.svg?react";
import { formatCfarServiceExpiryDate } from "@/utilities/hotels/cfar";

interface BookingDetailsProps {
  className?: string;
  reservation: Reservation;
  cfarContract?: CfarContract;
}

export const BookingDetails = ({
  reservation,
  className,
  cfarContract,
}: BookingDetailsProps) => {
  const { t, language } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "BookingDetails");
  const data = useBookingDetails(reservation);
  const hasCfar = cfarContract != null;

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>{t("bookingDetails")}</h3>

      <div className={cn("section")}>
        <div className={cn("field")}>
          <span className={cn("label")}>
            {t("tripSearch.confirmationCode")}
          </span>
          <span className={cn("value")}>{data.confirmationCode}</span>
        </div>
        {data.hotelReference !== "" && (
          <div className={cn("field")}>
            <span className={cn("label")}>
              {t("hotelTrips.hotelReference")}
            </span>
            <span className={cn("value")}>{data.hotelReference}</span>
          </div>
        )}
      </div>

      <div className={cn("section")}>
        <div className={cn("field")}>
          <span className={cn("label")}>{t("hotelTrips.checkingIn")}</span>
          <span className={cn("value")}>{data.primaryGuestName}</span>
        </div>
        {data.guestSummary !== "" && (
          <div className={cn("field")}>
            <span className={cn("label")}>{t("guests")}</span>
            <span className={cn("value")}>{data.guestSummary}</span>
          </div>
        )}
      </div>

      {data.email !== "" && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <span className={cn("label")}>{t("contactInformation")}</span>
            <span className={cn("value")}>{data.email}</span>
          </div>
        </div>
      )}

      {data.roomLabel !== "" && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <span className={cn("label")}>{t("roomDetails")}</span>
            <span className={cn("value", ["strong"])}>{data.roomLabel}</span>
          </div>
          {hasCfar && cfarContract?.exerciseExpiration != null ? (
            <div className={cn("cfar-badge")}>
              <div className={cn("cfar-title")}>
                <CfarTicketIcon className={cn("cfar-icon")} />
                <span>{t("selfServe.cancelForAnyReason")}</span>
              </div>
              <span className={cn("cfar-expiry")}>
                {t("selfServe.cfarExpiryUntil", {
                  expiryDate: formatCfarServiceExpiryDate(
                    cfarContract.exerciseExpiration,
                    language
                  ),
                })}
              </span>
            </div>
          ) : data.isRefundable ? (
            <div className={cn("badge", ["refundable"])}>
              {data.cancellationPrimaryText}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
